import { useState, useMemo, useCallback, Fragment } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
} from '@tanstack/react-table';
import {
  HeaderCell,
  TooltipCell,
  CheckboxCell,
  CheckboxHeader,
} from '@common/components/Table/Cells';
import { useColumnWidths } from '@common/components/Table/useColumnWidths';
import * as styles from '@common/components/Table/index.module.css';

function Table({
  data = [],
  columns = [],
  sorting = [],
  onSortingChange,
  selectedRowIds = {},
  onSelectionChange,
  selectAllMode = false,
  onSelectAllModeChange,
  deselectedRowIds = {},
  onDeselectedRowIdsChange,
  totalCount = 0,
  columnSizing = {},
  onColumnSizingChange,
  columnSizingInfo = {},
  onColumnSizingInfoChange,
  getRowId,
  isRowSelectable,
  onRowClick,
  // Optional row drill-down. When renderExpanded is provided, each row for which
  // isRowExpanded(row.original) is true gets a detail row rendered beneath it
  // spanning all columns. Omitting these keeps the table's original behavior.
  renderExpanded,
  isRowExpanded,
}) {
  const [isResizing, setIsResizing] = useState(false);

  const allRowsSelected = useMemo(() => {
    if (!data.length) return false;
    if (selectAllMode) {
      // In selectAllMode, all rows are selected unless they're in deselectedRowIds
      return data.every((row) => !deselectedRowIds[String(getRowId(row))]);
    }
    if (!Object.keys(selectedRowIds).length) return false;
    return data.every((row) => selectedRowIds[String(getRowId(row))]);
  }, [selectedRowIds, data, getRowId, selectAllMode, deselectedRowIds]);

  const someRowsSelected = useMemo(() => {
    if (!data.length) return false;
    if (selectAllMode) {
      // In selectAllMode, some rows are selected if any row is not deselected
      return data.some((row) => !deselectedRowIds[String(getRowId(row))]);
    }
    if (!Object.keys(selectedRowIds).length) return false;
    return data.some((row) => selectedRowIds[String(getRowId(row))]);
  }, [selectedRowIds, data, getRowId, selectAllMode, deselectedRowIds]);

  const handleSelectAll = useCallback(
    ({ target: { checked } }) => {
      if (!onSelectAllModeChange) return;
      onSelectAllModeChange(checked);

      // Clear deselectedRowIds when toggling selectAllMode
      if (onDeselectedRowIdsChange) {
        onDeselectedRowIdsChange({});
      }

      if (!onSelectionChange) return;
      onSelectionChange(
        data.reduce(
          (acc, row) => ({
            ...acc,
            [getRowId(row)]: checked,
          }),
          { ...selectedRowIds },
        ),
      );
    },
    [
      data,
      getRowId,
      selectedRowIds,
      onSelectionChange,
      onSelectAllModeChange,
      onDeselectedRowIdsChange,
    ],
  );

  const handleSelectRow = useCallback(
    (rowId, checked) => {
      if (selectAllMode) {
        if (!checked) {
          const wasAlreadyDeselected = !!deselectedRowIds[rowId];
          const newDeselectedRowIds = { ...deselectedRowIds, [rowId]: true };
          const deselectedCount = Object.keys(newDeselectedRowIds).filter(
            (key) => newDeselectedRowIds[key],
          ).length;

          if (
            !wasAlreadyDeselected &&
            deselectedCount === totalCount &&
            totalCount > 0
          ) {
            if (onSelectAllModeChange) {
              onSelectAllModeChange(false);
            }
            if (onDeselectedRowIdsChange) {
              onDeselectedRowIdsChange({});
            }
          } else if (onDeselectedRowIdsChange) {
            onDeselectedRowIdsChange(newDeselectedRowIds);
          }

          if (onSelectionChange) {
            const newSelected = { ...selectedRowIds };
            delete newSelected[rowId];
            onSelectionChange(newSelected);
          }
        } else {
          if (onDeselectedRowIdsChange) {
            const newDeselected = { ...deselectedRowIds };
            delete newDeselected[rowId];
            onDeselectedRowIdsChange(newDeselected);
          }
          const rowInCurrentPage = data.some(
            (row) => String(getRowId(row)) === String(rowId),
          );
          if (rowInCurrentPage && onSelectionChange) {
            onSelectionChange({ ...selectedRowIds, [rowId]: true });
          }
        }
      } else {
        if (!checked && onSelectAllModeChange) {
          onSelectAllModeChange(false);
        }
        if (onSelectionChange) {
          onSelectionChange({ ...selectedRowIds, [rowId]: checked });
        }
      }
    },
    [
      selectedRowIds,
      onSelectionChange,
      onSelectAllModeChange,
      selectAllMode,
      deselectedRowIds,
      onDeselectedRowIdsChange,
      data,
      getRowId,
      totalCount,
    ],
  );

  const checkboxHeaderRenderer = useCallback(
    () => (
      <CheckboxHeader
        allRowsSelected={allRowsSelected}
        someRowsSelected={someRowsSelected}
        handleSelectAll={handleSelectAll}
      />
    ),
    [allRowsSelected, someRowsSelected, handleSelectAll],
  );

  const checkboxCellRenderer = useCallback(
    ({ row }) => {
      const rowId = getRowId ? getRowId(row.original) : row.original.uuidHost;
      const stringRowId = String(rowId);
      const isSelected = selectAllMode
        ? !deselectedRowIds[stringRowId]
        : !!selectedRowIds[stringRowId];
      const isDisabled = isRowSelectable
        ? !isRowSelectable(row.original)
        : false;
      return (
        <CheckboxCell
          row={row}
          rowId={rowId}
          selectedRowIds={
            selectAllMode ? { [stringRowId]: isSelected } : selectedRowIds
          }
          handleSelectRow={handleSelectRow}
          disabled={isDisabled}
        />
      );
    },
    [
      getRowId,
      selectedRowIds,
      handleSelectRow,
      selectAllMode,
      deselectedRowIds,
      isRowSelectable,
    ],
  );

  const enhancedColumns = useMemo(
    () =>
      columns.map((column) => {
        const enhancedColumn = { ...column };

        if (column.staticWidth !== undefined) {
          enhancedColumn.enableResizing = false;
          enhancedColumn.enableColumnResizing = false;
        }

        if (column.id === 'select') {
          return {
            ...enhancedColumn,
            header: checkboxHeaderRenderer,
            cell: checkboxCellRenderer,
          };
        }
        return enhancedColumn;
      }),
    [columns, checkboxHeaderRenderer, checkboxCellRenderer],
  );

  const table = useReactTable({
    data,
    columns: enhancedColumns,
    state: {
      sorting,
      columnSizing,
      columnSizingInfo,
    },
    manualPagination: true,
    manualSorting: true,
    enableSorting: true,
    columnResizeMode: 'onChange',
    onSortingChange,
    onColumnSizingChange,
    onColumnSizingInfoChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId,
  });

  const headers = useMemo(
    () => table.getHeaderGroups()?.flatMap((group) => group.headers) || [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [table, enhancedColumns],
  );

  // Calculate column widths using custom hook
  const columnWidths = useColumnWidths(headers, columnSizing);

  return (
    <div className={styles['table-content']}>
      <table className={styles.table}>
        <colgroup>
          {columnWidths.widths.map(
            ({ header, minSize, percentage, width, isResized, isStatic }) => {
              // Use pixel width for static and resized columns, percentage for others
              const colStyle =
                isStatic || (isResized && width !== null)
                  ? {
                      width: `${width}px`,
                      minWidth: `${minSize}px`,
                    }
                  : {
                      width: `${percentage}%`,
                      minWidth: `${minSize}px`,
                    };

              return <col key={header.id} style={colStyle} />;
            },
          )}
        </colgroup>
        <thead className={styles['table-header']}>
          <tr className={styles['header-row']}>
            {headers.map((header) => (
              <HeaderCell
                key={header.id}
                header={header}
                sorting={sorting}
                onSortingChange={onSortingChange}
                isResizing={isResizing}
                setIsResizing={setIsResizing}
                cellClassName={styles['data-cell']}
              />
            ))}
          </tr>
        </thead>
        <tbody className={styles['table-body']}>
          {table.getRowModel().rows.map((row) => {
            const rowClassName = onRowClick
              ? `${styles['table-row']} ${styles['clickable-row']}`
              : styles['table-row'];

            const expanded =
              renderExpanded && isRowExpanded && isRowExpanded(row.original);

            return (
              <Fragment key={row.id}>
              <tr className={rowClassName}>
                {row.getVisibleCells().map((cell) => {
                  const { columnDef } = cell.column;
                  const { tooltip } = columnDef;
                  const isCheckbox = cell.column.id === 'select';
                  const isActions = cell.column.id === 'actions';

                  if (isCheckbox || isActions) {
                    return (
                      <td key={cell.id}>
                        <div
                          className={isActions ? styles['actions-cell'] : ''}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </div>
                      </td>
                    );
                  }

                  return (
                    <td
                      key={cell.id}
                      className={styles['data-cell']}
                      onClick={() => {
                        if (onRowClick) {
                          onRowClick(row.original);
                        }
                      }}
                    >
                      <TooltipCell
                        tooltipContent={tooltip}
                        tooltipPosition="bottom"
                        showOnHover
                        cellContext={cell.getContext()}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TooltipCell>
                    </td>
                  );
                })}
              </tr>
              {expanded && (
                <tr className={styles['expanded-row']}>
                  <td colSpan={row.getVisibleCells().length}>
                    {renderExpanded(row.original)}
                  </td>
                </tr>
              )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default Table;
